import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { LeaveService } from '../../../services/leave';
import { LeaveCancellationService } from '../../../services/leaveCancellationService';
import { LeaveAuditService } from '../../../services/leaveAuditService';
import { loadAllUsers, loadDirectReports, SimpleUser } from '../../../services/userLoader';
import { resolveActorId, sameIdentity } from '../../../services/identity';
import { leaveDaysFor } from '../../../services/leaveDuration';
import { LeaveRequest, LeaveStatus } from '../../../types/leave';
import { LeavePageHeader, isHrRole, isManagerRole } from './leaveShared';
import EmptyState from '../../common/EmptyState';
import '../LeavePage.css';
import './leavePages.css';
import '../../common/emsPage.css';

type Tab = 'MINE' | 'REQUESTS';

/**
 * Leave Cancellation.
 *
 * Two views in one page: an employee raises a cancellation against their own booked
 * leave, and a manager (or HR) decides the ones routed to them. Kept together because
 * they are two halves of one workflow — a manager is also an employee with leave of
 * their own.
 */
const LeaveCancellationPage: React.FC = () => {
  const { user } = useAuth();
  const canDecide = isManagerRole(user);
  const isHr = isHrRole(user);
  /*
   * userEmail is the ownership key — canWithdraw/canRequestCancellation compare it against the
   * leave's employeeEmail with sameIdentity, so it must stay an email and must NOT become a
   * Cognito sub, or nobody with an unset email attribute could cancel their own leave.
   *
   * actorId is what gets recorded. Both were userEmail before, so an account with no email wrote
   * '' as the actor and its withdrawals appeared in HR's Audit History with no "Changed By".
   */
  const userEmail = user?.email || '';
  const actorId = resolveActorId(user);

  const [tab, setTab] = useState<Tab>('MINE');
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [reports, setReports] = useState<SimpleUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Stable so `load` can depend on it without re-creating the loader every render.
  const showToast = useCallback((msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3600);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Settled, not all: the directory lookup hits Cognito and can fail on its own, and
      // when it does the viewer should still get their own leave list rather than an empty
      // page from one rejected promise.
      const [leaveResult, directoryResult] = await Promise.allSettled([
        LeaveService.listLeaves(),
        canDecide ? (isHr ? loadAllUsers() : loadDirectReports(userEmail)) : Promise.resolve([]),
      ]);

      if (leaveResult.status === 'fulfilled') {
        setLeaves(leaveResult.value);
      } else {
        setLeaves([]);
        showToast('Could not load your leave requests. Please retry.', 'error');
      }

      // An empty directory only narrows the approver queue, so it is worth a warning
      // rather than a failure — the manager would otherwise see "no requests" and
      // conclude their team had raised none.
      if (directoryResult.status === 'fulfilled') {
        setReports(directoryResult.value);
      } else {
        setReports([]);
        if (canDecide) showToast('Could not load your team directory; requests may be missing.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [canDecide, isHr, userEmail, showToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const evts = ['leave-requests-updated', 'leave-balance-updated', 'storage'];
    evts.forEach(e => window.addEventListener(e, load));
    return () => { evts.forEach(e => window.removeEventListener(e, load)); };
  }, [load]);

  /** The viewer's own leave that is still live enough to be worth cancelling. */
  const myLeaves = useMemo(() => {
    const keep = new Set([
      String(LeaveStatus.APPROVED).toUpperCase(),
      String(LeaveStatus.SUBMITTED).toUpperCase(),
      String(LeaveStatus.CANCELLATION_REQUESTED).toUpperCase(),
    ]);
    return leaves
      .filter(l => sameIdentity(l.employeeEmail, userEmail))
      .filter(l => keep.has(String(l.status).toUpperCase()))
      .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
  }, [leaves, userEmail]);

  /**
   * The viewer's leave that has already been withdrawn or cancelled.
   *
   * Withdrawing moves a request to CANCELLED, which myLeaves above deliberately excludes —
   * so the row the employee just acted on vanished from the page the instant they acted,
   * leaving a toast as the only evidence it ever happened. A few seconds later there was
   * nothing on screen to confirm the withdrawal, no record of the reason they typed, and
   * no way to tell "I withdrew it" apart from "it never saved". Settled requests belong on
   * the page that settles them.
   *
   * Capped and newest-first because this is a confirmation, not an archive — the full
   * history lives on Apply Leave.
   */
  const settledLeaves = useMemo(() => {
    /*
     * Withdrawn and cancelled both land on CANCELLED, so the status alone cannot tell them
     * apart. The audit trail can: withdraw() writes a 'Leave Withdrawn' entry synchronously
     * to the same local cache this reads, so the label is already correct by the time the
     * table re-renders. A cache miss (different browser, cleared storage) falls back to the
     * neutral 'Cancelled' rather than guessing wrong about who did it.
     */
    // entityId, not leaveId — the client field is the generic id of whatever the entry is
    // about; the server's leaveId is mapped onto it once inside the audit service.
    const withdrawnIds = new Set(
      LeaveAuditService.getAuditLogs()
        .filter(a => a.action === 'Leave Withdrawn')
        .map(a => a.entityId)
    );
    return leaves
      .filter(l => sameIdentity(l.employeeEmail, userEmail))
      .filter(l => String(l.status).toUpperCase() === String(LeaveStatus.CANCELLED).toUpperCase())
      .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''))
      .slice(0, 10)
      .map(l => ({ leave: l, withdrawn: withdrawnIds.has(l.id) }));
  }, [leaves, userEmail]);

  /** Cancellations awaiting this approver. HR sees all; a manager only their reports'. */
  const pending = useMemo(() => {
    const all = LeaveCancellationService.pendingCancellations(leaves);
    if (isHr) return all;
    return all.filter(l => reports.some(r => sameIdentity(r.email, l.employeeEmail)));
  }, [leaves, reports, isHr]);

  /*
   * One handler, two outcomes, decided by whether the manager has approved yet.
   *
   * Still Submitted -> withdraw outright: nobody approved it, so there is nothing for the
   * manager to confirm and no reason to make the employee wait. Already Approved -> the
   * cancellation goes to the manager, because taking back an absence they granted is their
   * decision. canWithdraw / canRequestCancellation are mutually exclusive by construction,
   * so this cannot pick both.
   */
  const handleRequest = async (leave: LeaveRequest) => {
    const withdrawGate = LeaveCancellationService.canWithdraw(leave, userEmail);
    const isWithdrawal = withdrawGate.allowed;

    if (!isWithdrawal) {
      const gate = LeaveCancellationService.canRequestCancellation(leave, userEmail);
      if (!gate.allowed) { showToast(gate.reason || 'This leave cannot be cancelled.', 'error'); return; }
    }

    const reason = window.prompt(
      isWithdrawal
        ? `Why are you withdrawing ${leave.type} on ${leave.startDate}?`
        : `Why are you cancelling ${leave.type} on ${leave.startDate}?`
    );
    if (reason === null) return;
    if (!reason.trim()) {
      showToast(isWithdrawal ? 'A withdrawal reason is required.' : 'A cancellation reason is required.', 'error');
      return;
    }

    setBusyId(leave.id);
    try {
      if (isWithdrawal) {
        await LeaveCancellationService.withdraw(leave, reason, userEmail, actorId);
        showToast('Request withdrawn — the days are back in your balance.', 'success');
      } else {
        await LeaveCancellationService.requestCancellation(leave, reason, userEmail, actorId);
        showToast('Cancellation request sent to your manager.', 'success');
      }
      await load();
    } catch (err: any) {
      showToast(err?.message || 'Could not submit the request.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDecision = async (leave: LeaveRequest, approve: boolean) => {
    let note: string | undefined;
    if (!approve) {
      const entered = window.prompt(`Why are you rejecting this cancellation for ${leave.employeeName}?`);
      if (entered === null) return;
      if (!entered.trim()) { showToast('A reason is required to reject.', 'error'); return; }
      note = entered.trim();
    }

    setBusyId(leave.id);
    try {
      await LeaveCancellationService.decideCancellation(leave, approve, actorId, note);
      showToast(
        approve
          ? `Cancelled — ${leaveDaysFor(leave)} day(s) returned to ${leave.employeeName}'s balance.`
          : 'Cancellation rejected; the leave stands.',
        'success'
      );
      await load();
    } catch (err: any) {
      showToast(err?.message || 'Could not record the decision.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  if (!user) return null;

  const statusPill = (s: string) => {
    const u = s.toUpperCase();
    if (u === 'APPROVED') return 'is-good';
    if (u === 'CANCELLATION REQUESTED') return 'is-warn';
    if (u === 'CANCELLED') return 'is-crit';
    return '';
  };

  return (
    <section className="leave-page">
      <LeavePageHeader title="Leave Cancellation" />

      {toast && <div className={`ems-toast ems-toast--${toast.type}`}>{toast.msg}</div>}

      {canDecide && (
        <nav className="ems-tabs" role="tablist" aria-label="Cancellation views">
          <button
            type="button" role="tab" aria-selected={tab === 'MINE'}
            className={`ems-tab ${tab === 'MINE' ? 'active' : ''}`} onClick={() => setTab('MINE')}
          >
            My Leave
          </button>
          <button
            type="button" role="tab" aria-selected={tab === 'REQUESTS'}
            className={`ems-tab ${tab === 'REQUESTS' ? 'active' : ''}`} onClick={() => setTab('REQUESTS')}
          >
            Cancellation Requests
            {pending.length > 0 && <span className="ems-tab-count">{pending.length}</span>}
          </button>
        </nav>
      )}

      {(!canDecide || tab === 'MINE') && (
        <div className="ems-card ems-card--table">
          <div className="ems-card-head" style={{ padding: '18px 22px 0' }}>
            <div>
              <h3>Your Leave</h3>
              <p className="ems-card-sub">
                Approved and submitted requests. Leave can only be cancelled before its start
                date — once it has begun, the days have been taken.
              </p>
            </div>
          </div>

          {loading ? (
            <p className="ems-empty">Loading…</p>
          ) : myLeaves.length === 0 ? (
            <EmptyState
              icon="inbox"
              title="Nothing to cancel"
              subtitle="You have no approved or pending leave requests at the moment."
            />
          ) : (
            <div className="ems-table-scroll">
              <table className="ems-table">
                <thead>
                  <tr>
                    <th>Leave Type</th><th>From</th><th>To</th><th>Days</th><th>Status</th><th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {myLeaves.map(l => {
                    // Withdraw (no approval needed) and Request cancellation (manager must
                    // agree) are mutually exclusive — see the service. Whichever applies
                    // supplies both the button label and, when neither does, the reason.
                    const withdrawGate = LeaveCancellationService.canWithdraw(l, userEmail);
                    const cancelGate = LeaveCancellationService.canRequestCancellation(l, userEmail);
                    const gate = withdrawGate.allowed ? withdrawGate : cancelGate;
                    const isWithdrawal = withdrawGate.allowed;
                    return (
                      <tr key={l.id}>
                        <td><span className="la-leave-type-badge">{l.type}</span></td>
                        {/* Two columns rather than one collapsed range, so From and To line up
                            down the table and match My Leave History on Apply Leave. */}
                        <td className="la-dates">{l.startDate}</td>
                        <td className="la-dates">{l.endDate}</td>
                        <td><strong>{leaveDaysFor(l)}</strong></td>
                        <td><span className={`lr-pill ${statusPill(String(l.status))}`}>{l.status}</span></td>
                        <td>
                          {gate.allowed ? (
                            <button
                              type="button" className="ems-btn ems-btn--warn"
                              disabled={busyId === l.id}
                              onClick={() => handleRequest(l)}
                              title={isWithdrawal
                                ? 'Not yet approved — withdrawing removes it immediately.'
                                : 'Already approved — your manager has to agree to the cancellation.'}
                            >
                              {busyId === l.id
                                ? (isWithdrawal ? 'Withdrawing…' : 'Sending…')
                                : (isWithdrawal ? 'Withdraw' : 'Request cancellation')}
                            </button>
                          ) : (
                            // Shown rather than hidden: a missing button teaches nothing,
                            // whereas the reason explains the rule.
                            <span className="lb-withdraw-blocked" title={gate.reason}>
                              {String(l.status).toUpperCase() === 'CANCELLATION REQUESTED'
                                ? 'Awaiting manager'
                                : 'Cannot cancel'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Proof the withdrawal happened.
          Acting on a request removes it from the table above, so without this the employee
          is left with a toast that has already gone and no way to confirm the request is
          actually gone rather than silently failed. Only rendered once something has been
          settled — an empty "nothing withdrawn" card would be noise on a page whose whole
          purpose is the table above. */}
      {(!canDecide || tab === 'MINE') && !loading && settledLeaves.length > 0 && (
        <div className="ems-card ems-card--table">
          <div className="ems-card-head" style={{ padding: '18px 22px 0' }}>
            <div>
              <h3>Withdrawn &amp; Cancelled</h3>
              <p className="ems-card-sub">
                These days are back in your balance and the dates are free again. Your most
                recent {settledLeaves.length === 1 ? 'outcome' : `${settledLeaves.length} outcomes`} —
                the full history is on Apply Leave.
              </p>
            </div>
          </div>

          <div className="ems-table-scroll">
            <table className="ems-table">
              <thead>
                <tr>
                  <th>Leave Type</th><th>From</th><th>To</th><th>Days</th><th>Outcome</th><th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {settledLeaves.map(({ leave: l, withdrawn }) => (
                  <tr key={l.id}>
                    <td><span className="la-leave-type-badge">{l.type}</span></td>
                    <td className="la-dates">{l.startDate}</td>
                    <td className="la-dates">{l.endDate}</td>
                    <td><strong>{leaveDaysFor(l)}</strong></td>
                    {/* "Withdrawn" when the employee pulled it themselves, which is the
                        outcome they are looking for confirmation of. Both are CANCELLED
                        underneath, so the word is the only thing distinguishing them. */}
                    <td>
                      <span className={`lr-pill ${withdrawn ? 'is-warn' : 'is-crit'}`}>
                        {withdrawn ? 'Withdrawn' : 'Cancelled'}
                      </span>
                    </td>
                    {/* The reason they typed into the prompt, read back. Without it there is
                        nothing anywhere in the UI that shows what they wrote. */}
                    <td>{l.decisionReason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canDecide && tab === 'REQUESTS' && (
        <div className="ems-card ems-card--table">
          <div className="ems-card-head" style={{ padding: '18px 22px 0' }}>
            <div>
              <h3>Cancellation Requests</h3>
              <p className="ems-card-sub">
                Approving returns the days to the employee's balance and frees the dates on the
                shared calendar.
              </p>
            </div>
          </div>

          {loading ? (
            <p className="ems-empty">Loading…</p>
          ) : pending.length === 0 ? (
            <EmptyState
              icon="inbox"
              title="No cancellation requests"
              subtitle="Requests from your team will appear here as soon as they are raised."
            />
          ) : (
            <div className="ems-table-scroll">
              <table className="ems-table">
                <thead>
                  <tr>
                    <th>Employee</th><th>Leave Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {pending.map(l => (
                    <tr key={l.id}>
                      <td>
                        <div className="la-emp-info">
                          <strong>{l.employeeName}</strong>
                          <span className="la-emp-email">{l.employeeEmail}</span>
                        </div>
                      </td>
                      <td><span className="la-leave-type-badge">{l.type}</span></td>
                      <td className="la-dates">{l.startDate}</td>
                      <td className="la-dates">{l.endDate}</td>
                      <td><strong>{leaveDaysFor(l)}</strong></td>
                      <td>{l.decisionReason || '—'}</td>
                      <td>
                        <div className="ems-actionbar" style={{ margin: 0 }}>
                          <button
                            type="button" className="ems-btn ems-btn--success"
                            disabled={busyId === l.id}
                            onClick={() => handleDecision(l, true)}
                          >
                            {busyId === l.id ? '…' : 'Approve'}
                          </button>
                          <button
                            type="button" className="ems-btn ems-btn--danger"
                            disabled={busyId === l.id}
                            onClick={() => handleDecision(l, false)}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default LeaveCancellationPage;
